if (!customElements.get('anniversary-timeline')) {
  class AnniversaryTimeline extends HTMLElement {
    connectedCallback() {
      this.initFrame = requestAnimationFrame(() => this.init());
    }

    disconnectedCallback() {
      cancelAnimationFrame(this.initFrame);
      this.controller?.abort();
      this.trackAnimation?.cancel();
      this.panelAnimation?.cancel();
    }

    init() {
      this.controller?.abort();
      this.controller = new AbortController();

      const { signal } = this.controller;

      this.track = this.querySelector(
        '.anniversary-timeline__tabs'
      );

      // Preserve the original chronological order.
      this.tabs = Array.from(
        this.querySelectorAll('[data-timeline-tab]')
      );

      this.panels = Array.from(
        this.querySelectorAll('[data-timeline-panel]')
      );

      if (!this.track || !this.tabs.length) return;

      this.style.setProperty(
        '--timeline-slot',
        `${100 / this.tabs.length}%`
      );

      this.tabs.forEach((tab, index) => {
        tab.addEventListener(
          'click',
          () => this.activate(index, { focus: true }),
          { signal }
        );

        tab.addEventListener(
          'keydown',
          (event) => this.handleKeydown(event, index),
          { signal }
        );
      });

      document.addEventListener(
        'shopify:block:select',
        (event) => {
          const index = this.tabs.findIndex(
            (tab) => tab.dataset.blockId === event.detail?.blockId
          );

          if (index !== -1) {
            this.activate(index);
          }
        },
        { signal }
      );

      const selectedIndex = this.tabs.findIndex(
        (tab) => tab.getAttribute('aria-selected') === 'true'
      );

      this.activate(selectedIndex >= 0 ? selectedIndex : 0, {
        animate: false
      });
    }

    activate(index, { animate = true, focus = false } = {}) {
      const activeTab = this.tabs[index];
      if (!activeTab) return;

      if (this.activeIndex === index) {
        if (focus) activeTab.focus({ preventScroll: true });
        return;
      }

      this.trackAnimation?.cancel();
      this.panelAnimation?.cancel();

      const firstTab = this.track.firstElementChild;

      const distance = firstTab
        ? activeTab.getBoundingClientRect().left -
          firstTab.getBoundingClientRect().left
        : 0;

      this.activeIndex = index;

      // Place the selected milestone first.
      // Earlier milestones move to the end.
      const orderedTabs = [
        ...this.tabs.slice(index),
        ...this.tabs.slice(0, index)
      ];

      orderedTabs.forEach((tab) => {
        this.track.appendChild(tab);
      });

      const panelId = activeTab.getAttribute('aria-controls');
      let activePanel = null;

      this.tabs.forEach((tab) => {
        const isActive = tab === activeTab;

        tab.classList.toggle('is-active', isActive);
        tab.setAttribute('aria-selected', String(isActive));
        tab.tabIndex = isActive ? 0 : -1;
      });

      this.panels.forEach((panel) => {
        const isActive = panel.id === panelId;

        panel.hidden = !isActive;

        if (isActive) activePanel = panel;
      });

      const reducedMotion = window.matchMedia(
        '(prefers-reduced-motion: reduce)'
      ).matches;

      if (animate && !reducedMotion) {
        if (distance > 0) {
          this.trackAnimation = this.track.animate(
            [
              { transform: `translateX(${distance}px)` },
              { transform: 'translateX(0)' }
            ],
            {
              duration: 420,
              easing: 'cubic-bezier(0.22, 1, 0.36, 1)'
            }
          );
        }

        if (activePanel) {
          // Animate the content separately so the dashed line stays fixed.
          this.panelAnimation = activePanel.animate(
            [
              { opacity: 0 },
              { opacity: 1 }
            ],
            {
              duration: 320,
              easing: 'ease-out'
            }
          );
        }
      }

      if (focus) {
        activeTab.focus({ preventScroll: true });
      }
    }

    handleKeydown(event, index) {
      let nextIndex;

      switch (event.key) {
        case 'ArrowRight':
          nextIndex = (index + 1) % this.tabs.length;
          break;

        case 'ArrowLeft':
          nextIndex =
            (index - 1 + this.tabs.length) % this.tabs.length;
          break;

        case 'Home':
          nextIndex = 0;
          break;

        case 'End':
          nextIndex = this.tabs.length - 1;
          break;

        default:
          return;
      }

      event.preventDefault();

      this.activate(nextIndex, { focus: true });
    }
  }

  customElements.define(
    'anniversary-timeline',
    AnniversaryTimeline
  );
}